/**
 * Run Diligence Modal
 * ===================
 * The input form for a company diligence run, and the cost line above the
 * button.
 *
 * Every field is optional except the website, and the website is only asked
 * for when the lead record has none. A person in a hurry fills nothing and
 * presses the button. Placeholder text shows what the diligence service will
 * use when a field is left empty.
 *
 * All five field groups render at once. Nothing is collapsed and there is no
 * toggle, so a validation message is always on screen next to its field.
 *
 * The company name is not a field. Editing it here would produce a report
 * about a company the lead record does not name.
 *
 * A re-run opens prefilled from the lead's latest diligence version, and a
 * line at the top of the form says which version the values came from. A retry
 * after a service error reuses that version's row and overwrites its 14 input
 * columns, so no earlier version holds what the operator typed. A blank form
 * would drop the registration number, the jurisdiction and the deal context in
 * silence, and the retry would then produce a weaker report than the attempt it
 * replaces.
 */

import React, { useState, useMemo } from 'react';
import { Modal, Button, Input, Textarea, Alert } from '../ui';
import { startDiligence } from '../../api/diligence';

// Whether the operator is offered a choice of research profile.
//
// Set to false on 2026-09-07, by the requester's decision. Quick runs one
// research provider and its own label calls it "not decision-grade", so every
// run is Standard for now. The radio block below is kept and gated rather than
// deleted: setting this to true offers the choice again. The backend still
// accepts `lite`, so nothing there changes either way.
const SHOW_PROFILE_CHOICE = false;

// The two profiles the portal offers. `deep` is deliberately absent: it is
// slower, dearer and needs a named operator the portal does not collect.
const PROFILES = [
  {
    value: 'standard',
    label: 'Standard',
    line: 'USD 8 to 12, about 27 minutes',
  },
  {
    value: 'lite',
    label: 'Quick',
    line: 'USD 1 to 3, about 8 minutes, one research provider, not decision-grade',
  },
];

// A field with no service default is left blank in the report brief. Saying so
// is more useful than an empty box.
const BLANK = 'Left blank when unknown';

// Group 2. A registration number is the input most likely to sharpen a
// report, so this group sits directly under the website.
const REGISTRATION_FIELDS = [
  { name: 'legal_name', label: 'Registered legal name', placeholder: BLANK },
  { name: 'jurisdiction', label: 'Jurisdiction of registration', placeholder: BLANK },
  { name: 'registration_number', label: 'Registration number', placeholder: BLANK },
  { name: 'ticker', label: 'Stock ticker', placeholder: BLANK },
  { name: 'lei', label: 'Legal Entity Identifier (LEI)', placeholder: BLANK },
];

// Groups 3, 4 and 5. Each placeholder is the value the diligence service uses
// when the field is empty.
const DEAL_CONTEXT_FIELDS = [
  { name: 'deal_type', label: 'Deal type', placeholder: 'General company assessment' },
  { name: 'proposed_relationship', label: 'Proposed relationship', placeholder: BLANK, multiline: true },
  {
    name: 'decision_to_inform',
    label: 'Decision this report informs',
    placeholder: 'Determine whether to progress to direct diligence',
    multiline: true,
  },
];

const SCOPE_FIELDS = [
  { name: 'geographic_scope', label: 'Geographic scope', placeholder: 'Global' },
  { name: 'sector_context', label: 'Sector context', placeholder: BLANK, multiline: true },
  { name: 'reporting_currency', label: 'Reporting currency', placeholder: 'USD' },
  { name: 'materiality', label: 'Materiality', placeholder: 'Use professional judgement', multiline: true },
];

const OUTPUT_FIELDS = [
  { name: 'confidentiality', label: 'Confidentiality marking', placeholder: 'Strictly private and confidential' },
  { name: 'decision_maker', label: 'Decision maker', placeholder: 'Senior deal team' },
];

const ALL_FIELDS = [
  ...REGISTRATION_FIELDS,
  ...DEAL_CONTEXT_FIELDS,
  ...SCOPE_FIELDS,
  ...OUTPUT_FIELDS,
];

const EMPTY_FORM = ALL_FIELDS.reduce((acc, f) => ({ ...acc, [f.name]: '' }), {});

/**
 * The form's opening values, taken from a previous run where it has them.
 *
 * Every field starts empty and is then overwritten by the previous run's value
 * for it. Nulls and blanks are dropped rather than written, so a field the
 * operator left empty last time opens empty again and keeps its placeholder.
 *
 * The website is absent on purpose. It lives on the lead record, not in this
 * map, and the form reads it from there.
 */
const prefillFrom = (previousRun) => {
  if (!previousRun) return EMPTY_FORM;
  const filled = { ...EMPTY_FORM };
  ALL_FIELDS.forEach((field) => {
    const value = previousRun[field.name];
    if (typeof value === 'string' && value.trim()) filled[field.name] = value;
  });
  return filled;
};

// Whether a prefill actually put anything on screen. A previous run that holds
// none of the 14 inputs must not show a note about values that are not there.
const hasPrefill = (form) => ALL_FIELDS.some((field) => Boolean(form[field.name]));

/**
 * Read per-field messages out of a failed start request.
 *
 * Two shapes reach here. The backend's own 422 carries
 * `detail.error.details.fields`, a list of `{ field, message }`. A request
 * body FastAPI itself rejects carries `detail` as a list of `{ loc, msg }`.
 * Both are mapped, so a message always lands against its field.
 */
const readFieldErrors = (err) => {
  const detail = err?.response?.data?.detail;

  const fields = detail?.error?.details?.fields;
  if (Array.isArray(fields)) {
    const mapped = {};
    fields.forEach((item) => {
      if (item?.field) mapped[item.field] = item.message || 'This value was not accepted.';
    });
    return mapped;
  }

  if (Array.isArray(detail)) {
    const mapped = {};
    detail.forEach((item) => {
      const name = Array.isArray(item?.loc) ? item.loc[item.loc.length - 1] : null;
      if (name) mapped[name] = item.msg || 'This value was not accepted.';
    });
    return mapped;
  }

  return {};
};

const readMessage = (err) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  const message = detail?.error?.message;
  if (typeof message === 'string' && message.trim()) return message;
  return 'The diligence run could not be started. Try again.';
};

const RunDiligenceModal = ({
  isOpen,
  onClose,
  leadId,
  companyName,
  leadWebsite,
  previousRun = null,
  onStarted,
}) => {
  // The values the form OPENED with, computed once when the modal mounts. The
  // card mounts it on open and unmounts it on close, so every open reads the
  // latest version afresh.
  const [initialForm] = useState(() => prefillFrom(previousRun));
  const [form, setForm] = useState(initialForm);
  const [website, setWebsite] = useState('');
  const [profile, setProfile] = useState('standard');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  // The lead's website wins. The field exists only so a lead without one can
  // supply it, so a lead that has one shows it read-only.
  const existingWebsite = (leadWebsite || '').trim();
  const websiteFromLead = existingWebsite.length > 0;
  const websiteValue = websiteFromLead ? existingWebsite : website;
  const websiteMissing = !websiteFromLead && website.trim().length === 0;

  // Whether the form opened with values from a previous version.
  //
  // Read off the OPENING values, never the live ones. A value the operator
  // types into a blank form would otherwise be attributed to a version that
  // did not supply it. A version number is needed to name the source too, so a
  // previous run without one shows no note. Every run the status route returns
  // carries one.
  const prefilled = hasPrefill(initialForm) && Boolean(previousRun?.version);

  const chosenProfile = useMemo(
    () => PROFILES.find((p) => p.value === profile) || PROFILES[0],
    [profile],
  );

  const handleChange = (name) => (e) => {
    const { value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setMessage(null);
    setFieldErrors({});

    // Empty strings are dropped rather than sent. An empty field means "use
    // the service default", and the backend reads a missing key that way.
    const payload = { profile };
    Object.entries(form).forEach(([name, value]) => {
      const trimmed = (value || '').trim();
      if (trimmed) payload[name] = trimmed;
    });
    if (!websiteFromLead) payload.website = website.trim();

    try {
      const response = await startDiligence(leadId, payload);
      if (onStarted) onStarted(response?.data || null);
      setForm(initialForm);
      setWebsite('');
      onClose();
    } catch (err) {
      setFieldErrors(readFieldErrors(err));
      setMessage(readMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const Component = field.multiline ? Textarea : Input;
    return (
      <Component
        key={field.name}
        label={field.label}
        placeholder={field.placeholder}
        value={form[field.name]}
        onChange={handleChange(field.name)}
        error={fieldErrors[field.name]}
        disabled={submitting}
        {...(field.multiline ? { rows: 2 } : {})}
      />
    );
  };

  const renderGroup = (heading, fields) => {
    const singleLine = fields.filter((f) => !f.multiline);
    const multiline = fields.filter((f) => f.multiline);
    return (
      <div>
        <h5 className="mb-3 text-sm font-medium text-gray-700 dark:text-[#A0A4A8]">
          {heading}
        </h5>
        <div className="space-y-4">
          {singleLine.length > 0 && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {singleLine.map(renderField)}
            </div>
          )}
          {multiline.map(renderField)}
        </div>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Run due diligence"
      size="lg"
      closeOnOverlayClick={!submitting}
      showCloseButton={!submitting}
      footer={
        <div className="flex w-full items-center justify-between gap-4">
          {/* The cost and the duration on one line, above the button. No
              typed confirmation: the spend is capped on the server. */}
          <p className="text-sm text-gray-600 dark:text-[#A0A4A8]">
            {chosenProfile.label}: {chosenProfile.line}
          </p>
          <div className="flex items-center gap-3">
            <Button variant="outline-primary" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              loading={submitting}
              disabled={submitting || websiteMissing}
            >
              Start diligence run
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-5">
        {message && <Alert variant="error">{message}</Alert>}

        {/* Says where the values came from, so nobody sends a report brief
            they did not read. Every field stays editable. */}
        {prefilled && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 dark:border-[#3A3C3E] dark:bg-[#2A2C2E]">
            <p className="text-sm text-gray-700 dark:text-[#A0A4A8]">
              These values are from version {previousRun.version}. Edit or clear
              any of them.
            </p>
          </div>
        )}

        {/* The company name, read-only. It is not a field. */}
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-[#6E7278]">
            Company
          </p>
          <p className="mt-1 text-base font-medium text-gray-900 dark:text-white">
            {companyName || 'This lead has no company name'}
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-[#6E7278]">
            The report is written about the company named on the lead. Edit the
            lead to change it.
          </p>
        </div>

        {/* Group 1 — company identity. */}
        <div>
          <Input
            label="Company website"
            required={!websiteFromLead}
            placeholder="https://example.com"
            value={websiteValue}
            onChange={(e) => {
              setWebsite(e.target.value);
              setFieldErrors((prev) => {
                if (!prev.website) return prev;
                const next = { ...prev };
                delete next.website;
                return next;
              });
            }}
            readOnly={websiteFromLead}
            disabled={submitting}
            error={fieldErrors.website}
          />
          {websiteFromLead ? (
            <p className="mt-1 text-xs text-gray-500 dark:text-[#6E7278]">
              This value comes from the lead record.
            </p>
          ) : (
            <p className="mt-1 text-xs text-gray-500 dark:text-[#6E7278]">
              This lead has no website. A diligence run needs one, and it is
              saved back to the lead.
            </p>
          )}
        </div>

        {/* Group 2 — registration. */}
        <div>
          <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
            Registration
          </h4>
          <p className="mb-3 text-xs text-gray-500 dark:text-[#6E7278]">
            Every field here is optional. Each one you fill in makes the
            company easier to identify.
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {REGISTRATION_FIELDS.map(renderField)}
          </div>
        </div>

        {/* The research profile. Hidden while SHOW_PROFILE_CHOICE is false. */}
        {SHOW_PROFILE_CHOICE && (
          <div>
            <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
              Research profile
            </h4>
            <div className="space-y-2">
              {PROFILES.map((option) => (
                <label
                  key={option.value}
                  className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                    profile === option.value
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-200 dark:border-[#3A3C3E]'
                  }`}
                >
                  <input
                    type="radio"
                    name="diligence-profile"
                    value={option.value}
                    checked={profile === option.value}
                    onChange={() => setProfile(option.value)}
                    disabled={submitting}
                    className="mt-1"
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-900 dark:text-white">
                      {option.label}
                    </span>
                    <span className="block text-xs text-gray-500 dark:text-[#A0A4A8]">
                      {option.line}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Groups 3, 4 and 5. All visible, no toggle. */}
        <div className="border-t border-gray-200 pt-4 dark:border-[#3A3C3E]">
          <p className="mb-4 text-xs text-gray-500 dark:text-[#6E7278]">
            Every field here is optional. Each one you fill in narrows what the
            report covers.
          </p>
          <div className="space-y-5">
            {renderGroup('Deal context', DEAL_CONTEXT_FIELDS)}
            {renderGroup('Scope', SCOPE_FIELDS)}
            {renderGroup('Output', OUTPUT_FIELDS)}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default RunDiligenceModal;
